// Seletor de período reutilizável: calendar range do shadcn dentro de um
// Popover, com atalhos rápidos (Hoje / 7 / 15 / 30 dias / Este mês). Não decide
// o período padrão — quem consome define o valor inicial.
import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";
import { format, startOfMonth, subDays } from "date-fns";
import { activeDateFnsLocale } from "../lib/locale-format.js";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import "../i18n.js";

// Atalhos: cada um devolve um range a partir de "hoje" (recebido pronto para não
// depender de Date.now() aqui e manter a função testável de fora).
function presets(today: Date, t: TFunction): { label: string; range: DateRange }[] {
  return [
    { label: t("dateRangePicker.today"), range: { from: today, to: today } },
    {
      label: t("dateRangePicker.days", { count: 7 }),
      range: { from: subDays(today, 6), to: today },
    },
    {
      label: t("dateRangePicker.days", { count: 15 }),
      range: { from: subDays(today, 14), to: today },
    },
    {
      label: t("dateRangePicker.days", { count: 30 }),
      range: { from: subDays(today, 29), to: today },
    },
    { label: t("dateRangePicker.thisMonth"), range: { from: startOfMonth(today), to: today } },
  ];
}

function label(range: DateRange | undefined, t: TFunction): string {
  if (!range?.from) return t("dateRangePicker.selectPeriod");
  const locale = activeDateFnsLocale();
  const fmt = (d: Date) => format(d, "dd MMM yyyy", { locale });
  if (!range.to || range.from.getTime() === range.to.getTime()) return fmt(range.from);
  return `${format(range.from, "dd MMM", { locale })} – ${fmt(range.to)}`;
}

export function DateRangePicker({
  value,
  onChange,
  className,
}: {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const today = new Date();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className={cn("justify-start gap-2 font-normal", className)}
            aria-label={t("dateRangePicker.selectPeriod")}
          />
        }
      >
        <CalendarIcon className="size-4 text-muted-foreground" />
        <span className="tabular-nums">{label(value, t)}</span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {presets(today, t).map((p) => (
            <Button
              type="button"
              key={p.label}
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(p.range);
                setOpen(false);
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <Calendar
          mode="range"
          locale={activeDateFnsLocale()}
          defaultMonth={value?.from}
          selected={value}
          onSelect={onChange}
          numberOfMonths={2}
          disabled={{ after: today }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
